import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  computed,
  Inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  ViewContainerRef,
  viewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  AiUserDataGroup,
  ConfigurationService,
  FeatureService,
  GlobalStore,
  UserService,
} from '@maximizer/core/shared/data-access';
import {
  CDN_URL,
  ConfigurationSetting,
  ConfigurationSettingCodes,
  InteractionListItem,
  User,
} from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  EntryType as OutlookEntryType,
  EventAction,
  EventJourney,
  EventModule,
  OutlookTimelineConfiguration,
  OutlookTimelineEvent,
  OutlookTimelineFilter,
  OutlookTimelineFilterConfiguration,
  OutlookTimelineModule,
  OutlookTimelineSummaryArgs,
  EntryType,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TimelineService } from '@maximizer/outlook/timeline/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { POPUP_CONTAINER, PopupService } from '@progress/kendo-angular-popup';
import { KENDO_SVGICON } from '@progress/kendo-angular-icons';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import {
  KENDO_TOOLTIP,
  TooltipDirective,
} from '@progress/kendo-angular-tooltip';
import {
  catchError,
  debounceTime,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
  takeUntil,
} from 'rxjs';
import { OutlookTimelineCardComponent } from '../timeline-card/timeline-card.component';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { UI_BUTTONS } from '@maximizer/core/shared/ui/components/buttons';
import { OUTLOOK_AI_UI } from '@maximizer/outlook/ai/ui';
import { OutlookAiApiService } from '@maximizer/outlook/ai/data-access';
import { AiFeaturesService, isOnForUser } from '@maximizer/ai/data-access';
import { TrackerService } from '@maximizer/outlook/shared/data-access';

interface TimelineForm {
  users: User[] | null;
  interaction: InteractionListItem[] | null;
}

type DropdownFilterType = 'users' | 'types';

@Component({
  selector: 'maximizer-outlook-timeline',
  templateUrl: './timeline.component.html',
  providers: [
    OutlookAiApiService,
    PopupService,
    {
      provide: POPUP_CONTAINER,
      useFactory: (elementRef: ElementRef) => elementRef,
      deps: [ElementRef],
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    FormsModule,
    KENDO_BUTTONS,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    KENDO_LOADER,
    KENDO_SVGICON,
    OUTLOOK_SHARED_UI,
    OutlookTimelineCardComponent,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
    UI_PIPES,
    UI_BUTTONS,
    KENDO_TOOLTIP,
    OUTLOOK_AI_UI,
  ],
})
export class TimelineComponent implements OnInit, OnDestroy {
  tooltips = viewChildren(TooltipDirective);
  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  type = input.required<string>();
  id = input.required<string>();

  isCompany = computed(
    () =>
      this.type() === OutlookEntryType.AddressBook &&
      globalThis.atob(this.id()).includes('Company'),
  );

  queryEntryType = computed<EntryType.AddressBook | EntryType.Lead>(() =>
    this.type() === OutlookEntryType.Lead
      ? EntryType.Lead
      : EntryType.AddressBook,
  );

  private readonly timelineFilterSettingCodes: ConfigurationSettingCodes = {
    code3: 'OutlookTimelineFilter',
    code4: 'Filters',
  };

  private readonly pageSize = 50;
  private readonly delayedSearchTime = 400;
  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private readonly minSearchLength = 3;
  private readonly usersDropdownOpen = signal(false);
  private readonly typesDropdownOpen = signal(false);
  private readonly hasOpenDropdown = computed(
    () => this.usersDropdownOpen() || this.typesDropdownOpen(),
  );

  private hasPendingFilterChanges = false;

  showFilterPopup = false;
  fullTimelineData: OutlookTimelineEvent[] = [];
  filteredTimelineData: OutlookTimelineEvent[] = [];
  groupedTimeline: OutlookTimelineModule[] | null = [];
  importantNotes: OutlookTimelineEvent[] | null = null;
  typesList: InteractionListItem[] = [];
  configuration: OutlookTimelineConfiguration | null = null;
  filterSettings: ConfigurationSetting | null = null;
  users: User[] = [];
  form: FormGroupWrapper<TimelineForm>;
  lastSearch?: string;
  nextPageKey: string | null = null;

  timelineSummaryShown = signal(false);
  timelineSummaryArgs?: OutlookTimelineSummaryArgs;

  baseFilter: OutlookTimelineFilter = {
    interactions: [],
    users: [],
  };

  loading = {
    page: false,
    results: false,
    more: false,
    filters: false,
  };

  // AI feature flags and settings
  outlookAiTimelineSummaryEnabled = signal(false);
  aiEnabled = signal(false);
  aiTimelineSummaryOnForUser = signal(false);
  aiTimelineSummarizationFeatureEnabled = signal(false);
  aiAnonymizerFeatureEnabled = signal(false);

  aiTimelineSummaryFeatureFlagsEnabled = computed(
    () =>
      this.outlookAiTimelineSummaryEnabled() &&
      this.aiTimelineSummarizationFeatureEnabled() &&
      this.aiAnonymizerFeatureEnabled(),
  );

  aiTimelineSummaryEnabled = computed(
    () =>
      this.aiEnabled() &&
      this.aiTimelineSummaryOnForUser() &&
      this.aiTimelineSummarizationFeatureEnabled() &&
      this.aiAnonymizerFeatureEnabled(),
  );

  constructor(
    @Inject(CDN_URL) public cdn: string,
    private readonly timelineService: TimelineService,
    private readonly userService: UserService,
    private readonly globalStore: GlobalStore,
    private readonly configurationService: ConfigurationService,
    private readonly aiFeaturesService: AiFeaturesService,
    private readonly feature: FeatureService,
    private readonly tracker: TrackerService,
    public readonly viewContainerRef: ViewContainerRef,
  ) {
    this.form = FormWrapperBuilder.group<TimelineForm>({
      users: null,
      interaction: null,
    });

    this.outlookAiTimelineSummaryEnabled.set(
      this.feature.isFeatureOn(
        'integration-o365-ai-timeline-summary-enabled',
        false,
      ),
    );

    this.aiTimelineSummarizationFeatureEnabled.set(
      this.feature.isFeatureOn('core-timeline-ai-summarization-shown', false),
    );

    this.aiAnonymizerFeatureEnabled.set(
      this.feature.isFeatureOn('core-ai-anonymizer-enabled', false),
    );
  }

  ngOnInit(): void {
    this.setupSearchSubject();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchSubject(): void {
    this.searchSubject
      .pipe(debounceTime(this.delayedSearchTime), takeUntil(this.destroy$))
      .subscribe((text) => {
        this.lastSearch = text ?? undefined;
        this.searchWithCurrentFilters();
      });
  }

  private loadData(): void {
    this.loading.page = true;

    forkJoin({
      configuration: this.timelineService.getConfiguration(),
      users: this.getActiveUsers(),
      setting: this.getFilterSettings(),
      aiEnabled: this.getAiFeaturesEnabled(),
    })
      .pipe(
        switchMap((data) => {
          this.configuration = data.configuration;
          this.users = data.users;
          this.filterSettings = data.setting;
          this.aiEnabled.set(data.aiEnabled ?? false);

          this.mapTypes();

          const timeline = this.hasNoQueryableCriteria(this.appliedFilter)
            ? of({
                data: [],
                nextPageKey: null,
              })
            : this.timelineService.forEntry(
                this.id(),
                this.queryEntryType(),
                this.appliedFilter,
                this.isCompany(),
                this.pageSize,
              );

          const aiFeatureSettings = data.aiEnabled
            ? this.getAIFeatureSettings()
            : of(null);

          return forkJoin({
            timeline: timeline,
            aiFeatureSettings: aiFeatureSettings,
          }).pipe(
            map(({ timeline, aiFeatureSettings }) => ({
              ...data,
              timeline,
              aiFeatureSettings,
            })),
          );
        }),
        catchError((error) => {
          console.error('Error loading timeline:', error);
          this.notification().show('load', 'error');
          this.fullTimelineData = [];
          this.nextPageKey = null;
          this.filteredTimelineData = [];
          return of(null);
        }),
        finalize(() => (this.loading.page = false)),
      )
      .subscribe((data) => {
        if (!data) return;

        this.configuration = data.configuration;
        this.users = data.users;
        this.filterSettings = data.setting;
        this.fullTimelineData = data.timeline.data;
        this.nextPageKey = data.timeline.nextPageKey ?? null;
        this.filteredTimelineData = this.fullTimelineData;

        this.updateTimelineValues(data.timeline.data);

        const aiSettings = data.aiFeatureSettings;
        this.aiTimelineSummaryOnForUser.set(
          !!aiSettings &&
            isOnForUser({
              orgAvailable: aiSettings.admin.aiFeaturesEnabled.summarization,
              userEnabled: aiSettings.user.aiFeaturesEnabled.summarization,
            }),
        );
      });
  }

  private getActiveUsers(): Observable<User[]> {
    return this.userService.getActiveUsers().pipe(
      catchError((error) => {
        console.error('Error loading users for the timeline filter:', error);
        return of(this.users);
      }),
    );
  }

  private getAiFeaturesEnabled(): Observable<boolean> {
    if (!this.aiTimelineSummaryFeatureFlagsEnabled()) {
      return of(false);
    }

    return this.aiFeaturesService.getEnabled().pipe(
      catchError((error) => {
        console.error('Error loading AI feature availability:', error);
        return of(false);
      }),
    );
  }

  private getAIFeatureSettings(): Observable<AiUserDataGroup | null> {
    const session = this.globalStore.session();
    if (!session) {
      return of(null);
    }

    return this.aiFeaturesService
      .getAIUserData(session)
      .pipe(catchError(() => of(null)));
  }

  private getFilterSettings(): Observable<ConfigurationSetting | null> {
    const session = this.globalStore.session();

    if (session) {
      this.timelineFilterSettingCodes.code2 = session.user.id;

      return this.configurationService
        .getSetting(this.timelineFilterSettingCodes)
        .pipe(
          catchError(() => of(null)),
          tap((setting) => {
            this.filterSettings = setting;
          }),
        );
    }

    return of(null);
  }

  private updateFilterSettings(filter: OutlookTimelineFilter): void {
    if (!this.timelineFilterSettingCodes.code2) {
      return;
    }

    const settingFilter: OutlookTimelineFilterConfiguration = {
      interactions: filter.interactions ?? [],
      users: filter.users,
    };

    const setting = JSON.stringify(settingFilter);

    this.configurationService
      .updateSetting(
        this.filterSettings?.id ?? null,
        this.timelineFilterSettingCodes,
        setting,
      )
      .pipe(
        tap((savedSetting) => {
          this.filterSettings = savedSetting;
        }),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  private groupByYearAndMonth(
    events: OutlookTimelineEvent[],
  ): OutlookTimelineModule[] {
    const groupedData: OutlookTimelineModule[] = [];

    events.forEach((item) => {
      let dateValue: Date;
      if (typeof item.date.value === 'string') {
        dateValue = new Date(item.date.value);
      } else {
        dateValue = item.date.value;
      }
      if (Number.isNaN(dateValue.getTime())) {
        return;
      }

      const year = dateValue.getFullYear();
      const month = dateValue.getMonth() + 1;

      const date = groupedData.find(
        (item) => item.date == year + '-' + month + '-1',
      );
      if (date) {
        date.events.push(item);
      } else {
        groupedData.push({
          date: year + '-' + month + '-1',
          events: [item],
        });
      }
    });

    return groupedData ?? [];
  }

  refresh(): void {
    this.loadData();
  }

  onSearchInput(value: string): void {
    if (value.length >= this.minSearchLength || value.length === 0) {
      this.searchSubject.next(value);
    }
  }

  private searchWithCurrentFilters(): void {
    if (this.hasNoQueryableCriteria(this.appliedFilter)) {
      this.nextPageKey = null;
      this.fullTimelineData = [];
      this.filteredTimelineData = [];
      this.updateTimelineValuesWithSearch([]);
      return;
    }

    this.loading.results = true;
    this.nextPageKey = null;
    this.fullTimelineData = [];

    this.timelineService
      .forEntry(
        this.id(),
        this.queryEntryType(),
        this.appliedFilter,
        this.isCompany(),
        this.pageSize,
      )
      .pipe(
        catchError((error) => {
          console.error('Error searching the timeline:', error);
          this.notification().show('load', 'error');
          return of({ data: [], nextPageKey: null });
        }),
        finalize(() => (this.loading.results = false)),
      )
      .subscribe((data) => {
        if (!data) return;
        this.fullTimelineData = data.data;
        this.nextPageKey = data.nextPageKey ?? null;
        this.updateTimelineValuesWithSearch(this.fullTimelineData);
      });
  }

  private get appliedFilter(): OutlookTimelineFilter {
    return this.lastSearch?.trim()
      ? { ...this.baseFilter, searchPhrase: this.lastSearch }
      : this.baseFilter;
  }

  private hasNoQueryableCriteria(filter: OutlookTimelineFilter): boolean {
    const hasUsers = filter.users.length > 0;
    const hasInteractions = (filter.interactions?.length ?? 0) > 0;
    const hasSearchPhrase = (filter.searchPhrase?.trim().length ?? 0) > 0;

    return !hasUsers && !hasInteractions && !hasSearchPhrase;
  }

  toggleFilterPopup(forceToggle?: boolean): void {
    this.closeTooltips();

    if (typeof forceToggle === 'boolean') {
      this.showFilterPopup = forceToggle;
      return;
    }
    this.showFilterPopup = !this.showFilterPopup;
  }

  mapTypes(): void {
    if (this.configuration) {
      this.typesList = this.configuration.types.map((interaction) => ({
        key: 'type-' + interaction.type,
        id: interaction.type,
        type: 'interaction',
        name: interaction.display,
        items: undefined,
        icon: '',
        tag: this.formatInteractionTag(interaction.display),
      }));
    }

    this.applyInitialSelections();
  }

  private applyInitialSelections(): void {
    const savedFilter = this.getSavedFilter();

    if (savedFilter) {
      this.applySavedSelections(savedFilter);
      this.baseFilter = this.getSelectedFilter();
      this.hasPendingFilterChanges = false;
      this.updateTimelineValuesWithSearch(this.fullTimelineData);
      return;
    }

    const defaultFilter = this.getDefaultFilter();
    this.applySavedSelections(defaultFilter);
    this.baseFilter = this.getSelectedFilter();
    this.hasPendingFilterChanges = false;
    this.updateTimelineValuesWithSearch(this.fullTimelineData);
    this.updateFilterSettings(defaultFilter);
  }

  private getSavedFilter(): OutlookTimelineFilter | null {
    const textValue = this.filterSettings?.textValue;

    if (!textValue) {
      return null;
    }

    try {
      const saved = JSON.parse(
        textValue,
      ) as Partial<OutlookTimelineFilterConfiguration>;

      return {
        interactions: Array.isArray(saved.interactions)
          ? saved.interactions
          : [],
        users: Array.isArray(saved.users) ? saved.users : [],
      };
    } catch {
      return null;
    }
  }

  private getDefaultFilter(): OutlookTimelineFilter {
    return {
      interactions: this.typesList.map((item) => String(item.id)),
      users: this.users.map((user) => user.key),
    };
  }

  private applySavedSelections(filter: OutlookTimelineFilter): void {
    const selectedUsers = filter.users?.length
      ? this.users.filter((user) => filter.users?.includes(String(user.key)))
      : null;

    const selectedInteractions = filter.interactions.length
      ? this.typesList.filter((type) =>
          filter.interactions.includes(`${type.id}`),
        )
      : null;

    this.form.group.get('users')?.setValue(selectedUsers, { emitEvent: false });
    this.form.group
      .get('interaction')
      ?.setValue(selectedInteractions, { emitEvent: false });
  }

  formatInteractionTag(name: string): string {
    if (!name) return '';
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }

  private closeTooltips(): void {
    this.tooltips().forEach((tooltip) => tooltip.hide());
  }

  onDropdownFilterChange(filter: DropdownFilterType): void {
    this.hasPendingFilterChanges = true;

    if (!this.isDropdownFilterOpen(filter)) {
      this.saveFilters();
    }
  }

  private isDropdownFilterOpen(filter: DropdownFilterType): boolean {
    return filter === 'types'
      ? this.typesDropdownOpen()
      : this.usersDropdownOpen();
  }

  onDropdownFilterOpen(filter: DropdownFilterType): void {
    this.closeTooltips();
    this.setDropdownFilterOpen(filter, true);
  }

  onDropdownFilterClose(filter: DropdownFilterType): void {
    this.setDropdownFilterOpen(filter, false);
    this.applyFiltersWhenDropdownsClosed();
  }

  private setDropdownFilterOpen(
    filter: DropdownFilterType,
    isOpen: boolean,
  ): void {
    if (filter === 'types') {
      this.typesDropdownOpen.set(isOpen);
      return;
    }

    this.usersDropdownOpen.set(isOpen);
  }

  private applyFiltersWhenDropdownsClosed(): void {
    if (this.hasPendingFilterChanges && !this.hasOpenDropdown()) {
      this.saveFilters();
    }
  }

  private saveFilters(): void {
    this.loading.results = true;

    const selectedUserKeys = this.getSelectedUsers().map((user) => user.key);

    this.getActiveUsers()
      .pipe(
        switchMap((latestUsers) => {
          this.users = latestUsers;
          this.reconcileSelectedUsers(selectedUserKeys);
          const reconciledUserKeys = this.getSelectedUsers().map(
            (user) => user.key,
          );
          this.baseFilter = this.getSelectedFilter(reconciledUserKeys);
          this.updateFilterSettings(this.baseFilter);

          this.fullTimelineData = [];
          this.filteredTimelineData = [];
          this.nextPageKey = null;

          if (this.hasNoQueryableCriteria(this.appliedFilter)) {
            return of({ data: [], nextPageKey: null });
          }

          return this.timelineService.forEntry(
            this.id(),
            this.queryEntryType(),
            this.appliedFilter,
            this.isCompany(),
            this.pageSize,
          );
        }),
        catchError((error) => {
          console.error('Error applying timeline filters:', error);
          this.notification().show('load', 'error');
          return of({ data: [], nextPageKey: null });
        }),
        finalize(() => {
          this.hasPendingFilterChanges = false;
          this.loading.results = false;
        }),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.fullTimelineData = result.data;
        this.nextPageKey = result.nextPageKey ?? null;

        this.updateTimelineValuesWithSearch(this.fullTimelineData);
      });
  }

  private getSelectedUsers(): User[] {
    return this.form.controls.users.value ?? [];
  }

  private getSelectedFilter(userKeys?: string[]): OutlookTimelineFilter {
    const selectedUsers =
      userKeys ?? this.getSelectedUsers().map((user) => user.key);
    const selectedInteractionItems = this.form.group.get('interaction')
      ?.value as InteractionListItem[] | null;
    const selectedInteractions = selectedInteractionItems
      ? selectedInteractionItems.map((item) => item.id as string)
      : [];

    const filter: OutlookTimelineFilter = {
      interactions: selectedInteractions,
      users: selectedUsers,
    };

    return filter;
  }

  private reconcileSelectedUsers(selectedUserKeys: string[]): void {
    if (!selectedUserKeys.length) {
      this.form.group.get('users')?.setValue(null, { emitEvent: false });
      return;
    }

    const selectedUsers = this.users.filter((user) =>
      selectedUserKeys.includes(user.key),
    );

    this.form.group
      .get('users')
      ?.setValue(selectedUsers.length > 0 ? selectedUsers : null, {
        emitEvent: false,
      });
  }

  private updateTimelineValuesWithSearch(
    timelineData: OutlookTimelineEvent[],
  ): void {
    this.filteredTimelineData = timelineData;
    this.updateTimelineValues(timelineData);
  }

  private updateTimelineValues(filteredTimeline: OutlookTimelineEvent[]): void {
    const grouped = this.groupByYearAndMonth(filteredTimeline);
    this.groupedTimeline = grouped.length > 0 ? grouped : null;

    const important = filteredTimeline.filter((o) => o.important);
    this.importantNotes = important.length > 0 ? important : null;
  }

  openTimelineSummary(): void {
    this.tracker.trackEvent({
      module: EventModule.AiTimelineSummary,
      journey: EventJourney.Open,
      action: EventAction.Clicked,
    });

    this.timelineSummaryArgs = this.getTimelineSummaryArgs();
    this.timelineSummaryShown.set(true);
  }

  private getTimelineSummaryArgs(): OutlookTimelineSummaryArgs {
    return {
      key: this.id(),
      filter: { ...this.appliedFilter },
    };
  }

  showMore(): void {
    if (
      !this.queryEntryType() ||
      !this.nextPageKey ||
      this.hasNoQueryableCriteria(this.appliedFilter)
    )
      return;

    this.loading.more = true;

    this.timelineService
      .forEntry(
        this.id(),
        this.queryEntryType(),
        this.appliedFilter,
        this.isCompany(),
        this.pageSize,
        this.nextPageKey,
      )
      .pipe(
        catchError((error) => {
          console.error('Error loading more timeline results:', error);
          this.notification().show('load', 'error');
          // null keeps nextPageKey intact so the user can retry Show more.
          return of(null);
        }),
        finalize(() => (this.loading.more = false)),
      )
      .subscribe((result) => {
        if (!result) return;

        this.fullTimelineData = [
          ...(this.fullTimelineData ?? []),
          ...result.data,
        ];

        this.nextPageKey = result.nextPageKey ?? null;

        this.updateTimelineValuesWithSearch(this.fullTimelineData);
      });
  }
}
